import AddressCapture from '@/components/generic/AddressCapture'
import WorkhistoryCapture from '@/components/generic/WorkHistoryCapture'
import ErrorDisplayComponent from '@/components/generic/ErrorDisplayComponent'
import TermsControl from '@/components/generic/customer/TermsControl'
import PropertyDisclosureControl from '@/components/generic/customer/PropertyDisclosureControl'
import ValidateBeforeSubmitMixin from '@/mixins/generic/validateBeforeSubmit'

import { mapActions, createNamespacedHelpers } from 'vuex'
const mapUserGetters = createNamespacedHelpers('user').mapGetters
const mapConfigGetters = createNamespacedHelpers('config').mapGetters

// A date-only "YYYY-MM-DD" string is parsed as UTC midnight by the language, which lands inside today for
// every negative UTC offset - so a business established tomorrow would read as not-future for every US
// dealer. Parse that shape as local instead, so it can be compared against a local boundary. The API
// currently sends the full "2026-08-12T00:00:00" form, which already parses as local.
function toLocalDate (value) {
  if (value instanceof Date) {
    return value
  }
  const dateOnly = typeof value === 'string' ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(value) : null
  if (dateOnly) {
    return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
  }
  return new Date(value)
}

// Single source of truth for "is this in the future", shared by the established-date picker predicate
// and the step-1 computed. They enforce the same rule from opposite ends, so if the two definitions
// drift the form can reach a state the user cannot satisfy - a date the picker offers but the step
// gate then rejects. Missing and unparseable values report false: absence is the required rule's job,
// and reporting it here too would put two different errors on screen for one empty field.
function isFutureDate (value) {
  const candidate = toLocalDate(value)
  if (isNaN(candidate.getTime())) {
    return false
  }
  const endOfToday = new Date()
  endOfToday.setHours(23, 59, 59, 999)
  return candidate > endOfToday
}

var mixin = {
  components: {
    'address-capture': AddressCapture,
    'work-history-capture': WorkhistoryCapture,
    'error-display-component': ErrorDisplayComponent,
    'terms-control': TermsControl,
    'property-disclosure-control': PropertyDisclosureControl
  },
  mixins: [ValidateBeforeSubmitMixin],
  data () {
    return {
      mobile: window.innerWidth <= 700,
      isMember: document.cookie.indexOf('LIM=ONE') > -1,
      isLoading: false,
      selectedFinancialStatementType: null,
      selectedBusinessType: null,
      selectedFinanceMethodType: null,
      selectedStateOfIncorporation: null,
      selectedMaritalStatus: null,
      selectedEmploymentStatus: null,
      selectedSpouseEmploymentStatus: null,
      currentStep: 0,
      termsVisible: false,
      isSaving: false,
      serverErrors: [],
      serverErrorMessage: '',
      preventClick: false,
      loadingBaseData: false,
      dealerId: 0,
      hasSaved: false,
      defaultDate: new Date('01/01/1970'),
      datePickerOptions: {
        disabledDate (date) {
          return date.getFullYear() > new Date().getFullYear() - 18
        },
        defaultView: 'year'
      },
      // Deliberately NOT datePickerOptions above - that one enforces an 18-years-ago minimum for date of
      // birth, which would wrongly block any business established in the last 18 years. This only stops
      // the future.
      noFutureDatePickerOptions: {
        disabledDate (date) {
          return isFutureDate(date)
        },
        defaultView: 'year'
      },
      bulmaSteps: undefined,
      previousCustomerAddresses: [],
      addresses: [],
      spouseAddresses: [],
      workHistory: [],
      spouseWorkHistory: [],
      residenceMonthsValid: true,
      spouseResidenceMonthsValid: true,
      residenceValidated: false,
      spouseResidenceValidated: false,
      residenceError: '',
      spouseResidenceError: '',
      workHistoryMonthsValid: true,
      spouseWorkHistoryMonthsValid: true,
      workHistoryValidated: false,
      spouseWorkHistoryValidated: false,
      workHistoryError: '',
      spouseWorkHistoryError: ''
    }
  },
  computed: {
    ...mapUserGetters(['currentUser']),
    ...mapConfigGetters(['definedTypes']),
    isOfficePhoneAreaCodeValid: function () {
      if (this.currentCustomer) {
        const office = this.currentCustomer.office ? this.currentCustomer.office : ''
        const officeAreaCode = office.slice(3, 6)
        if (officeAreaCode === '800' || officeAreaCode === '866' || officeAreaCode === '877' || officeAreaCode === '555') {
          return {
            valid: false,
            errorCode: 0,
            errorMessage: 'Invalid Office Area Code. Please enter an area code that is not one of the following (800, 866, 877, 555).'
          }
        }
        return {
          valid: true,
          errorCode: 1,
          errorMessage: ''
        }
      }
      return {
        valid: true,
        errorCode: 1,
        errorMessage: ''
      }
    },
    /// The picker cannot produce a future date, but a preloaded application can already hold one, so the
    /// value is re-checked here and gates the step the same way the office area code does.
    isBusinessEstablishedDateValid: function () {
      const established = this.currentCustomer ? this.currentCustomer.businessEstablishedDate : null

      // Empty and unparseable values are the required rule's business, not this one; isFutureDate reports
      // both as not-future, so there is deliberately no separate guard for them here.
      if (isFutureDate(established)) {
        return {
          valid: false,
          errorCode: 0,
          errorMessage: 'The business established date cannot be in the future. Please correct the date before continuing.'
        }
      }

      return { valid: true, errorCode: 1, errorMessage: '' }
    },
    stateOptions: function () {
      return this.definedTypes.usStateLookup.options
    },
    isValidationRequired: function () {
      return true
    },
    thisValidator: function () {
      return (this && this.$validator) ? this.$validator : null
    },
    isCreditTermsAccepted: function () {
      return this.currentCustomer ? this.currentCustomer.isCreditTermsAccepted : false
    },
    creditApplicationCompleted: function () {
      return this.currentCustomer ? this.currentCustomer.isCreditApplicationComplete : false
    },
    currentScope: function () {
      return this.availableScopes[this.currentStep]
    },
    workHistoryStepCount: function () {
      let stepCount = 5

      if (!this.showWorkStep) {
        stepCount--
      }

      return stepCount
    },
    spouseInfoStepCount: function () {
      let stepCount = 6

      if (!this.showWorkStep) {
        stepCount--
      }

      return stepCount
    },
    spouseWorkStepCount: function () {
      let stepCount = 8

      if (!this.showWorkStep) {
        stepCount--
      }

      if (!this.showSpouseStep) {
        stepCount--
      }

      if (!this.showSpouseResidence) {
        stepCount--
      }

      return stepCount
    },
    bankInfoStepCount: function () {
      let stepCount = 9

      if (!this.showWorkStep) {
        stepCount--
      }

      if (!this.showSpouseStep) {
        stepCount--
      }

      if (!this.showSpouseResidence) {
        stepCount--
      }

      if (!this.showSpouseWork) {
        stepCount--
      }

      return stepCount
    },
    isPrimaryAddressValid: function () {
      const numberOfPrimaryAddresses = this.addresses.filter((x) => x.isPrimary).length
      if (numberOfPrimaryAddresses === 1) {
        return {
          valid: true,
          errorCode: 1,
          errorMessage: ''
        }
      }
      if (numberOfPrimaryAddresses === 0) {
        return {
          valid: false,
          errorCode: 0,
          errorMessage: 'Please select your primary addresses below.'
        }
      } else if (numberOfPrimaryAddresses > 1) {
        return {
          valid: false,
          errorCode: 0,
          errorMessage: 'You can only have one your primary address.'
        }
      }
      return {
        valid: true,
        errorCode: 1,
        errorMessage: ''
      }
    },
    isSpousePrimaryAddressValid: function () {
      const numberOfPrimaryAddresses = this.spouseAddresses.filter((x) => x.isPrimary).length
      if (numberOfPrimaryAddresses === 1) {
        return {
          valid: true,
          errorCode: 1,
          errorMessage: ''
        }
      }
      if (numberOfPrimaryAddresses === 0) {
        return {
          valid: false,
          errorCode: 0,
          errorMessage: 'Please select your spouse\'s primary addresses below.'
        }
      } else if (numberOfPrimaryAddresses > 1) {
        return {
          valid: false,
          errorCode: 0,
          errorMessage: 'Your spouse can only have one primary address.'
        }
      }
      return {
        valid: true,
        errorCode: 1,
        errorMessage: ''
      }
    },
    isResidenceMonthsValid: function () {
      const months = this.totalResidenceMonths
      if (months < 36) {
        return {
          valid: false,
          errorCode: 0,
          errorMessage: 'You are required to provide at least 3 years of residence history.'
        }
      } else if (months > 1199) {
        return {
          valid: false,
          errorCode: 0,
          errorMessage: 'The total amount of months lived at a specific address cannot exceed 1199. The years and months fields are combined to calculate the total months of occupation at a specific address.'
        }
      } else if (months > this.calculateAgeInMonths) {
        return {
          valid: false,
          errorCode: 0,
          errorMessage: 'The total amount of months lived at a specific address cannot exceed age.'
        }
      }
      return {
        valid: true,
        errorCode: 1,
        errorMessage: ''
      }
    },
    totalResidenceMonths: function () {
      let totalMonths = 0
      if (this.currentCustomer && this.addresses && this.addresses.length > 0) {
        this.addresses.forEach(function (x) {
          var years = parseInt(x.years) || 0
          var months = parseInt(x.months) || 0
          totalMonths += (years * 12) + months
        })
      }
      return totalMonths
    },
    totalSpouseResidenceMonths: function () {
      let totalMonths = 0
      if (this.currentCustomer && this.spouseAddresses && this.spouseAddresses.length > 0) {
        this.spouseAddresses.forEach(function (x) {
          const years = parseInt(x.years) || 0
          const months = parseInt(x.months) || 0
          totalMonths += (years * 12) + months
        })
      }
      return totalMonths
    },
    totalSpouseWorkHistoryMonths: function () {
      let totalMonths = 0
      if (this.currentCustomer && this.spouseWorkHistory && this.spouseWorkHistory.length > 0) {
        this.spouseWorkHistory.forEach(function (x) {
          const years = parseInt(x.years) || 0
          const months = parseInt(x.months) || 0
          totalMonths += (years * 12) + months
        })
      }
      return totalMonths
    },
    isWorkHistoryMonthsValid: function () {
      if (this.selectedEmploymentStatus && (this.selectedEmploymentStatus.id === 0 || this.selectedEmploymentStatus.id === 3 || this.selectedEmploymentStatus.id === 6)) {
        const months = this.totalWorkHistoryMonths
        if (months < 24) {
          return {
            valid: false,
            errorCode: 0,
            errorMessage: 'You are required to provide at least 2 years of employment history.'
          }
        } else if (months > 1199) {
          return {
            valid: false,
            errorCode: 0,
            errorMessage: 'The total amount of months employed at a specific employer cannot exceed 1199. The years and months fields are combined to calculate the total months of employment at a specific employer.'
          }
        }
      }
      return {
        valid: true,
        errorCode: 1,
        errorMessage: ''
      }
    },
    isWorkHistoryEmployerValid: function () {
      if (this.selectedEmploymentStatus && (this.selectedEmploymentStatus.id === 0 || this.selectedEmploymentStatus.id === 3)) {
        if (this.workHistory && this.workHistory.length > 0) {
          for (var i = 0; i < this.workHistory.length; i++) {
            if (this.workHistory[i].employer === this.workHistory[i].address1) {
              return {
                valid: false,
                errorCode: 0,
                errorMessage: 'Invalid Employer Name. Please check each of your provided Employment History records.'
              }
            }
          }
        }
      }
      return {
        valid: true,
        errorCode: 1,
        errorMessage: ''
      }
    },
    isWorkHistoryPhoneAreaCodeValid: function () {
      if (this.selectedEmploymentStatus && (this.selectedEmploymentStatus.id === 0 || this.selectedEmploymentStatus.id === 3 || this.selectedEmploymentStatus.id === 5 || this.selectedEmploymentStatus.id === 6 || this.selectedEmploymentStatus.id === 7)) {
        if (this.workHistory && this.workHistory.length > 0) {
          for (var i = 0; i < this.workHistory.length; i++) {
            const office = this.workHistory[i].employerOffice
            const officeAreaCode = office.slice(3, 6)
            if (officeAreaCode === '800' || officeAreaCode === '866' || officeAreaCode === '877' || officeAreaCode === '555') {
              return {
                valid: false,
                errorCode: 0,
                errorMessage: 'Invalid Employer Area Code. Please enter an area code that is not one of the following (800, 866, 877, 555).'
              }
            }
          }
        }
      }
      return {
        valid: true,
        errorCode: 1,
        errorMessage: ''
      }
    },
    isSpouseResidenceMonthsValid: function () {
      const months = this.totalSpouseResidenceMonths
      if (months < 36) {
        return {
          valid: false,
          errorCode: 0,
          errorMessage: 'You are required to provide at least 3 years of residence history.'
        }
      } else if (months > 1199) {
        return {
          valid: false,
          errorCode: 0,
          errorMessage: 'The total amount of months lived at a specific address cannot exceed 1199. The years and months fields are combined to calculate the total months of occupation at a specific address.'
        }
      }
      return {
        valid: true,
        errorCode: 1,
        errorMessage: ''
      }
    },
    isSpouseWorkHistoryMonthsValid: function () {
      if (this.selectedSpouseEmploymentStatus) {
        if (this.selectedSpouseEmploymentStatus.id === 0 || this.selectedSpouseEmploymentStatus.id === 3) {
          const months = this.totalSpouseWorkHistoryMonths
          if (months < 24) {
            return {
              valid: false,
              errorCode: 0,
              errorMessage: 'You are required to provide at least 2 years of employment history.'
            }
          } else if (months > 1199) {
            return {
              valid: false,
              errorCode: 0,
              errorMessage: 'The total amount of months employed at a specific employer cannot exceed 1199. The years and months fields are combined to calculate the total months of employment at a specific employer.'
            }
          }
        }
      }
      return {
        valid: true,
        errorCode: 1,
        errorMessage: ''
      }
    },
    isSpouseWorkHistoryEmployerValid: function () {
      if (this.selectedSpouseEmploymentStatus && (this.selectedSpouseEmploymentStatus.id === 0 || this.selectedSpouseEmploymentStatus.id === 3 || this.selectedSpouseEmploymentStatus.id === 6)) {
        if (this.spouseWorkHistory && this.spouseWorkHistory.length > 0) {
          for (var i = 0; i < this.spouseWorkHistory.length; i++) {
            if (this.spouseWorkHistory[i].employer === this.spouseWorkHistory[i].address1) {
              return {
                valid: false,
                errorCode: 0,
                errorMessage: 'Invalid Spouse Employer Name. Please check each of your provided Spouse Employment History records.'
              }
            }
          }
        }
      }
      return {
        valid: true,
        errorCode: 1,
        errorMessage: ''
      }
    },
    isSpouseWorkHistoryPhoneAreaCodeValid: function () {
      if (this.selectedSpouseEmploymentStatus && (this.selectedSpouseEmploymentStatus.id === 0 || this.selectedSpouseEmploymentStatus.id === 3 || this.selectedSpouseEmploymentStatus.id === 5 || this.selectedSpouseEmploymentStatus.id === 6 || this.selectedSpouseEmploymentStatus.id === 7)) {
        if (this.spouseWorkHistory && this.spouseWorkHistory.length > 0) {
          for (var i = 0; i < this.spouseWorkHistory.length; i++) {
            const office = this.spouseWorkHistory[i].employerOffice
            const officeAreaCode = office.slice(3, 6)
            if (officeAreaCode === '800' || officeAreaCode === '866' || officeAreaCode === '877' || officeAreaCode === '555') {
              return {
                valid: false,
                errorCode: 0,
                errorMessage: 'Invalid Spouse Employer Phone Area Code. Please enter an area code that is not one of the following (800, 866, 877, 555).'
              }
            }
          }
        }
      }
      return {
        valid: true,
        errorCode: 1,
        errorMessage: ''
      }
    },
    isSpousePhoneAreaCodeValid: function () {
      if (this.currentCustomer && this.currentCustomer.spouse) {
        const home = this.currentCustomer.spouse.home ? this.currentCustomer.spouse.home : ''
        const homeAreaCode = home.slice(3, 6)
        if (homeAreaCode === '800' || homeAreaCode === '866' || homeAreaCode === '877' || homeAreaCode === '555') {
          return {
            valid: false,
            errorCode: 0,
            errorMessage: 'Invalid Spouse Home Area Code. Please enter an area code that is not one of the following (800, 866, 877, 555).'
          }
        }
        return {
          valid: true,
          errorCode: 1,
          errorMessage: ''
        }
      }
      return {
        valid: true,
        errorCode: 1,
        errorMessage: ''
      }
    },
    isSpouseMobilePhoneAreaCodeValid: function () {
      if (this.currentCustomer && this.currentCustomer.spouse) {
        const mobile = this.currentCustomer.spouse.mobile ? this.currentCustomer.spouse.mobile : ''
        const mobileAreaCode = mobile.slice(3, 6)
        if (mobileAreaCode === '800' || mobileAreaCode === '866' || mobileAreaCode === '877' || mobileAreaCode === '555') {
          return {
            valid: false,
            errorCode: 0,
            errorMessage: 'Invalid Spouse Mobile Area Code. Please enter an area code that is not one of the following (800, 866, 877, 555).'
          }
        }
        return {
          valid: true,
          errorCode: 1,
          errorMessage: ''
        }
      }
      return {
        valid: true,
        errorCode: 1,
        errorMessage: ''
      }
    },
    showWorkStep: function () {
      return this.selectedEmploymentStatus && this.selectedEmploymentStatus.name !== 'Unemployed'
    },
    showSpouseStep: function () {
      return this.spouseInfoRequired
    },
    showSpouseResidence: function () {
      return this.spouseInfoRequired
    },
    showSpouseWork: function () {
      return this.spouseInfoRequired && this.isSpouseEmployed
    },
    showRemoveAddress: function () {
      return this.checkRemoveButtonVisibility(this.addresses)
    },
    showRemoveSpouseAddress: function () {
      return this.checkRemoveButtonVisibility(this.spouseAddresses)
    },
    showRemoveWorkHistory: function () {
      return this.checkRemoveButtonVisibility(this.workHistory)
    },
    showRemoveSpouseWorkHistory: function () {
      return this.checkRemoveButtonVisibility(this.spouseWorkHistory)
    },
    workHistoryHeading: function () {
      var employmentHeading = ''
      if (this.selectedEmploymentStatus) {
        switch (this.selectedEmploymentStatus.id) {
          case 0:
          case 3:
            employmentHeading += 'Provide 2 Years of Work History'
            break
          case 1:
          case 2:
          case 4:
            employmentHeading += 'Provide Income Details'
            break
          case 5:
          case 7:
            employmentHeading += 'Provide Institution Details'
            break
          case 6:
            employmentHeading += 'Provide Self Employment History'
            break
          default:
            employmentHeading = ''
            break
        }
      }
      return employmentHeading
    },
    workHistorySubHeading: function () {
      var employmentSubHeading = ''
      if (this.selectedEmploymentStatus) {
        switch (this.selectedEmploymentStatus.id) {
          case 0:
          case 3:
            employmentSubHeading = 'Most recent first'
            break
          case 1:
          case 2:
          case 4:
            employmentSubHeading = ''
            break
          case 5:
          case 7:
            employmentSubHeading = ''
            break
          case 6:
            employmentSubHeading = 'Most recent first'
            break
          default:
            employmentSubHeading = ''
            break
        }
      }
      return employmentSubHeading
    },
    spouseWorkHistoryHeading: function () {
      var employmentHeading = ''
      if (this.selectedSpouseEmploymentStatus) {
        switch (this.selectedSpouseEmploymentStatus.id) {
          case 0:
          case 3:
            employmentHeading += 'Spouse: Provide 2 Years of Work History'
            break
          case 1:
          case 2:
          case 4:
            employmentHeading += 'Spouse: Provide Income Details'
            break
          case 5:
          case 7:
            employmentHeading += 'Spouse: Provide Institution Details'
            break
          case 6:
            employmentHeading += 'Spouse: Provide Self Employment History'
            break
          default:
            employmentHeading = ''
            break
        }
      }
      return employmentHeading
    },
    spouseWorkHistorySubHeading: function () {
      var employmentSubHeading = ''
      if (this.selectedSpouseEmploymentStatus) {
        switch (this.selectedSpouseEmploymentStatus.id) {
          case 0:
          case 3:
            employmentSubHeading = 'Most recent first'
            break
          case 1:
          case 2:
          case 4:
            employmentSubHeading = ''
            break
          case 5:
          case 7:
            employmentSubHeading = ''
            break
          case 6:
            employmentSubHeading = 'Most recent first'
            break
          default:
            employmentSubHeading = ''
            break
        }
      }
      return employmentSubHeading
    },
    calculateAge: function () {
      if (!this.currentCustomer) return 0
      if (!this.currentCustomer.dateOfBirth) return 0
      const dateOfBirth = new Date(this.currentCustomer.dateOfBirth)
      var diffMs = new Date() - dateOfBirth.getTime()
      var ageDt = new Date(diffMs)
      return Math.abs(ageDt.getUTCFullYear() - 1970)
    },
    calculateAgeInMonths: function () {
      return this.calculateAge * 12
    },
    calculateSpouseAge: function () {
      if (!this.currentCustomer) return 0
      if (!this.currentCustomer.spouse) return 0
      if (!this.currentCustomer.spouse.dateOfBirth) return 0
      const dateOfBirth = new Date(this.currentCustomer.spouse.dateOfBirth)
      var diffMs = new Date() - dateOfBirth.getTime()
      var ageDt = new Date(diffMs)
      return Math.abs(ageDt.getUTCFullYear() - 1970)
    },
    calculateSpouseAgeInMonths: function () {
      return this.calculateSpouseAge * 12
    },
    spouseInfoRequired: function () {
      return (this.isMarried && this.isCopState)
    },
    isCopState: function () {
      if (!this.addresses) return false
      if (this.addresses.length === 0) return false
      let primaryAddress = this.addresses.find(x => x.isPrimary)
      if (!primaryAddress) {
        primaryAddress = this.addresses[0]
      }
      return primaryAddress ? primaryAddress.isCopState : false
    },
    communityPropertyDisclosureRequired: function () {
      return this.isCopState
    },
    isMarried: function () {
      return this.selectedMaritalStatus ? this.selectedMaritalStatus.id === 1 : false
    },
    isSpouseEmployed: function () {
      return this.selectedSpouseEmploymentStatus && this.selectedSpouseEmploymentStatus.name !== 'Unemployed'
    },
    primaryAddress: function () {
      let primaryAddress = this.addresses.find(x => x.isPrimary)
      if (!primaryAddress) {
        primaryAddress = this.addresses[0]
      }
      return primaryAddress
    },
    isUser: function () {
      return this.currentUser !== null && this.currentUser !== undefined && this.currentUser.roles.length > 0
    }
  },
  methods: {
    ...mapActions(['updateReturnUrl', 'updateIsContinueLater']),
    visibilityChanged (newValue, oldValue) {
      this.termsVisible = newValue
    },
    checkRemoveButtonVisibility (dataCollection) {
      if (dataCollection.length > 1) {
        return true
      }
      return false
    },
    async validateStep (e) {
      this.preventClick = false
      this.$validator.validate(this.currentScope + '.*').then((result) => {
        if (result === false) {
          if (this.isUser || this.isMember) return
          this.preventClick = true
          this.$goToFirstValidationError(this.$validator.errors.items.filter((x) => x.scope === this.currentScope))
        }
      })
    },
    validateResidenceHistory () {
      this.residenceValidated = true
      const months = this.totalResidenceMonths
      if (months < 36) {
        this.residenceMonthsValid = false
        this.residenceError = 'You are required to provide at least 3 years of residence history.'
        if (this.isUser || this.isMember) return []
        return ['Error occurred']
      } else if (months > 1199) {
        this.residenceMonthsValid = false
        this.residenceError = 'The total amount of months lived at a specific address cannot exceed 1199. The years and months fields are combined to calculate the total months of occupation at a specific address.'
        if (this.isUser || this.isMember) return []
        return ['Error occurred']
      } else if (months > this.calculateAgeInMonths) {
        this.residenceMonthsValid = false
        this.residenceError = 'The total amount of months lived at a specific address cannot exceed age.'
        if (this.isUser || this.isMember) return []
        return ['Error occurred']
      }
      this.residenceMonthsValid = true
      return []
    },
    validateSpouseResidenceHistory () {
      this.spouseResidenceValidated = true
      const months = this.totalSpouseResidenceMonths
      if (months < 36) {
        this.spouseResidenceMonthsValid = false
        this.spouseResidenceError = 'You are required to provide at least 3 years of residence history.'
        if (this.isUser || this.isMember) return []
        return ['Error occurred']
      } else if (months > 1199) {
        this.spouseResidenceMonthsValid = false
        this.spouseResidenceError = 'The total amount of months lived at a specific address cannot exceed 1199. The years and months fields are combined to calculate the total months of occupation at a specific address.'
        if (this.isUser || this.isMember) return []
        return ['Error occurred']
      }
      this.spouseResidenceMonthsValid = true
      return []
    },
    validateWorkHistory () {
      this.workHistoryValidated = true

      if (this.selectedEmploymentStatus.id === 0 || this.selectedEmploymentStatus.id === 3 || this.selectedEmploymentStatus.id === 6) {
        const months = this.totalWorkHistoryMonths
        if (months < 24) {
          this.workHistoryMonthsValid = false
          this.workHistoryError = 'You are required to provide at least 2 years of employment history.'
          if (this.isUser || this.isMember) return []
          return ['Error occurred']
        } else if (months > 1199) {
          this.workHistoryMonthsValid = false
          this.workHistoryError = 'The total amount of months employed at a specific employer cannot exceed 1199. The years and months fields are combined to calculate the total months of employment at a specific employer.'
          if (this.isUser || this.isMember) return []
          return ['Error occurred']
        }
      }
      this.workHistoryMonthsValid = true
      return []
    },
    validateSpouseWorkHistory () {
      if (this.selectedSpouseEmploymentStatus) {
        this.spouseWorkHistoryValidated = true
        if (this.selectedSpouseEmploymentStatus.id === 0 || this.selectedSpouseEmploymentStatus.id === 3 || this.selectedSpouseEmploymentStatus.id === 6) {
          const months = this.totalSpouseWorkHistoryMonths
          if (months < 24) {
            this.spouseWorkHistoryError = 'You are required to provide at least 2 years of employment history.'
            this.spouseWorkHistoryMonthsValid = false
            if (this.isUser || this.isMember) return []
            return ['Error occurred']
          } else if (months > 1199) {
            this.spouseWorkHistoryMonthsValid = false
            this.spouseWorkHistoryError = 'The total amount of months employed at a specific employer cannot exceed 1199. The years and months fields are combined to calculate the total months of employment at a specific employer.'
            if (this.isUser || this.isMember) return []
            return ['Error occurred']
          }
        }
        this.spouseWorkHistoryMonthsValid = true
        return []
      }
      return []
    },
    onAutoSelectPrimaryAddress (address) {
      const primary = this.addresses.find((x) => x.isPrimary && x.id !== address.id)
      if (!primary) {
        const newPrimary = this.addresses.find((x) => !x.isPrimary && x.id !== address.id)
        if (newPrimary) {
          newPrimary.isPrimary = true
        }
      }
    },
    onUnselectNonPrimary (address) {
      if (address.isPrimary) {
        this.addresses.forEach((x) => {
          if (x.id !== address.id) {
            x.isPrimary = false
          }
        })
      }
    },
    onAutoSelectSpousePrimaryAddress (address) {
      const primary = this.spouseAddresses.find((x) => x.isPrimary && x.id !== address.id)
      if (!primary) {
        const newPrimary = this.spouseAddresses.find((x) => !x.isPrimary && x.id !== address.id)
        if (newPrimary) {
          newPrimary.isPrimary = true
        }
      }
    },
    onUnselectSpouseNonPrimary (address) {
      if (address.isPrimary) {
        this.spouseAddresses.forEach((x) => {
          if (x.id !== address.id) {
            x.isPrimary = false
          }
        })
      }
    },
    saveAddress: function (customerModel) {
      this.addresses.forEach(function (item) {
        var address = {
          id: isNaN(item.id) ? 0 : item.id,
          address1: item.address1,
          address2: item.address2,
          address3: item.address3,
          address4: item.address4,
          city: item.city,
          country: item.country,
          postalCode: item.postalCode,
          state: item.state,
          gpsAddress: item.gpsAddress,
          ownershipType: item.ownershipType ? item.ownershipType.name : '',
          years: item.years,
          months: item.months,
          rate: item.rate,
          isPrimary: item.isPrimary
        }

        customerModel.addresses.push(address)
      }, this)
    },
    saveWorkHistory: function (customerModel) {
      this.workHistory.forEach(function (item) {
        var history = {
          id: isNaN(item.id) ? 0 : item.id,
          employer: item.employer,
          jobTitle: item.jobTitle,
          employerOffice: item.employerOffice,
          address1: item.address1,
          address2: item.address2,
          address3: item.address3,
          address4: item.address4,
          city: item.city,
          country: item.country,
          postalCode: item.postalCode,
          state: item.state,
          gpsAddress: item.gpsAddress,
          years: item.years === null || item.years === undefined || item.years === '' ? 0 : +item.years,
          months: item.months === null || item.months === undefined || item.months === '' ? 0 : +item.months,
          grossIncome: item.grossIncome,
          otherIncome: item.otherIncome,
          otherIncomeSource: item.otherIncomeSource
        }

        customerModel.workHistory.push(history)
      }, this)
    },
    saveSpouseAddress: function (spouseModel) {
      this.spouseAddresses.forEach(function (item) {
        var address = {
          id: isNaN(item.id) ? 0 : item.id,
          address1: item.address1,
          address2: item.address2,
          address3: item.address3,
          address4: item.address4,
          city: this.$lodash.startCase(item.city),
          country: item.country,
          postalCode: item.postalCode,
          state: item.state,
          gpsAddress: item.gpsAddress,
          ownershipType: item.ownershipType ? item.ownershipType.name : '',
          years: item.years === null || item.years === undefined || item.years === '' ? 0 : +item.years,
          months: item.months === null || item.months === undefined || item.months === '' ? 0 : +item.months,
          rate: item.rate,
          isPrimary: item.isPrimary
        }

        spouseModel.addresses.push(address)
      }, this)
    },
    saveSpouseWorkHistory: function (spouseModel) {
      this.spouseWorkHistory.forEach(function (item) {
        var history = {
          id: isNaN(item.id) ? 0 : item.id,
          employer: item.employer,
          jobTitle: item.jobTitle,
          employerOffice: item.employerOffice,
          address1: item.address1,
          address2: item.address2,
          address3: item.address3,
          address4: item.address4,
          city: this.$lodash.startCase(item.city),
          country: item.country,
          postalCode: item.postalCode,
          state: item.state,
          gpsAddress: item.gpsAddress,
          years: item.years === null || item.years === undefined || item.years === '' ? 0 : +item.years,
          months: item.months === null || item.months === undefined || item.months === '' ? 0 : +item.months,
          grossIncome: item.grossIncome,
          otherIncome: item.otherIncome,
          otherIncomeSource: item.otherIncomeSource
        }
        spouseModel.workHistory.push(history)
      }, this)
    },
    addAddress: function (configuration) {
      const isSpouseAddress = configuration ? configuration.spouseAddress : false
      if (isSpouseAddress) {
        var spouseConfig = {
          sectionId: this.spouseAddresses.length + 100,
          loadCustomerOnly: configuration ? configuration.loadCustomerOnly : false,
          spouseAddress: true
        }
        var spouseAddress = this.createGenericAddress()
        spouseAddress.config = spouseConfig
        this.spouseAddresses.push(spouseAddress)
        if (this.spouseAddresses.length === 1) {
          this.spouseAddresses[0].isPrimary = true
        }
      } else {
        var config = {
          sectionId: this.addresses.length + 100,
          loadCustomerOnly: configuration ? configuration.loadCustomerOnly : false
        }
        var address = this.createGenericAddress()
        address.config = config
        this.addresses.push(address)
        if (this.addresses.length === 1) {
          this.addresses[0].isPrimary = true
        }
      }
    },
    addWorkHistory: function (configuration) {
      const isSpouseWorkHistory = configuration ? configuration.spouseWorkHistory : false
      if (isSpouseWorkHistory) {
        var spouseConfig = {
          sectionId: this.spouseWorkHistory.length + 100,
          loadCustomerOnly: configuration ? configuration.loadCustomerOnly : false
        }

        var spouseHistory = this.createGenericWorkHistory()
        spouseHistory.config = spouseConfig
        this.spouseWorkHistory.push(spouseHistory)
      } else {
        var config = {
          sectionId: this.workHistory.length + 100,
          loadCustomerOnly: configuration ? configuration.loadCustomerOnly : false
        }

        var history = this.createGenericWorkHistory()
        history.config = config
        this.workHistory.push(history)
      }
    },
    removeAddress (addressToRemove) {
      const isSpouseAddress = addressToRemove && addressToRemove.config ? addressToRemove.config.spouseAddress : false
      if (isSpouseAddress) {
        const indexToDelete = this.spouseAddresses.findIndex((x) => x.id === addressToRemove.id)
        this.spouseAddresses.splice(indexToDelete, 1)
        const primary = this.spouseAddresses.find((x) => x.isPrimary && x.id !== addressToRemove.id)
        if (!primary) {
          const newPrimary = this.spouseAddresses.find((x) => !x.isPrimary && x.id !== addressToRemove.id)
          if (newPrimary) {
            newPrimary.isPrimary = true
          }
        }
      } else {
        const indexToDelete = this.addresses.findIndex((x) => x.id === addressToRemove.id)
        this.addresses.splice(indexToDelete, 1)
        const primary = this.addresses.find((x) => x.isPrimary && x.id !== addressToRemove.id)
        if (!primary) {
          const newPrimary = this.addresses.find((x) => !x.isPrimary && x.id !== addressToRemove.id)
          if (newPrimary) {
            newPrimary.isPrimary = true
          }
        }
      }
    },
    removeWorkHistory: function (configuration) {
      var sectionId
      var offset = 100
      const isSpouseWorkHistory = configuration ? configuration.spouseWorkHistory : false
      if (isSpouseWorkHistory) {
        sectionId = this.spouseWorkHistory.length + offset - 1
        var spouseWorkHistoryFiltered = this.spouseWorkHistory.filter(function (el) {
          return el.config.sectionId !== sectionId
        })
        this.spouseWorkHistory = spouseWorkHistoryFiltered
      } else {
        sectionId = this.workHistory.length + offset - 1
        var workHistoryFiltered = this.workHistory.filter(function (el) {
          return el.config.sectionId !== sectionId
        })
        this.workHistory = workHistoryFiltered
      }
    },
    createGenericAddress (configuration) {
      return {
        id: this.$uuid.v4(),
        key: this.$uuid.v4(),
        address1: '',
        address2: '',
        address3: '',
        address4: '',
        city: '',
        country: '',
        postalCode: '',
        state: '',
        gpsAddress: '',
        ownershipType: null,
        isPrimary: false,
        years: 0,
        months: 0,
        rate: null
      }
    },
    createGenericWorkHistory (configuration) {
      return {
        id: this.$uuid.v4(),
        employer: '',
        jobTitle: '',
        employerOffice: '',
        address1: '',
        address2: '',
        address3: '',
        address4: '',
        city: '',
        country: '',
        postalCode: '',
        state: '',
        gpsAddress: '',
        years: 0,
        months: 0,
        grossIncome: null,
        otherIncome: 0,
        otherIncomeSource: '',
        employmentStatusType: 0,
        otherIncomeSourceType: 0
      }
    }
  },
  watch: {
    companyName: function (newValue, oldValue) {
      const titleCaseVersion = (newValue) ? this.$toTitleCase(newValue) : newValue
      if (titleCaseVersion !== newValue) {
        this.companyName = titleCaseVersion
      }
    },
    'currentCustomer.companyName': function (newValue, oldValue) {
      const titleCaseVersion = (newValue) ? this.$toTitleCase(newValue) : newValue
      if (titleCaseVersion !== newValue) {
        this.currentCustomer.companyName = titleCaseVersion
      }
    },
    businessBankName: function (newValue, oldValue) {
      const titleCaseVersion = (newValue) ? this.$toTitleCase(newValue) : newValue
      if (titleCaseVersion !== newValue) {
        this.businessBankName = titleCaseVersion
      }
    },
    businessBankContactName: function (newValue, oldValue) {
      const titleCaseVersion = (newValue) ? this.$toTitleCase(newValue) : newValue
      if (titleCaseVersion !== newValue) {
        this.businessBankContactName = titleCaseVersion
      }
    },
    'currentCustomer.firstName': function (newValue, oldValue) {
      const titleCaseVersion = (newValue) ? this.$toTitleCase(newValue) : newValue
      if (titleCaseVersion !== newValue) {
        this.currentCustomer.firstName = titleCaseVersion
      }
    },
    'currentCustomer.lastName': function (newValue, oldValue) {
      const titleCaseVersion = (newValue) ? this.$toTitleCase(newValue) : newValue
      if (titleCaseVersion !== newValue) {
        this.currentCustomer.lastName = titleCaseVersion
      }
    },
    'currentCustomer.spouse.firstName': function (newValue, oldValue) {
      const titleCaseVersion = (newValue) ? this.$toTitleCase(newValue) : newValue
      if (titleCaseVersion !== newValue) {
        this.currentCustomer.spouse.firstName = titleCaseVersion
      }
    },
    'currentCustomer.spouse.lastName': function (newValue, oldValue) {
      const titleCaseVersion = (newValue) ? this.$toTitleCase(newValue) : newValue
      if (titleCaseVersion !== newValue) {
        this.currentCustomer.spouse.lastName = titleCaseVersion
      }
    },
    'currentCustomer.businessBankName': function (newValue, oldValue) {
      const titleCaseVersion = (newValue) ? this.$toTitleCase(newValue) : newValue
      if (titleCaseVersion !== newValue) {
        this.currentCustomer.businessBankName = titleCaseVersion
      }
    },
    'currentCustomer.businessBankContactName': function (newValue, oldValue) {
      const titleCaseVersion = (newValue) ? this.$toTitleCase(newValue) : newValue
      if (titleCaseVersion !== newValue) {
        this.currentCustomer.businessBankContactName = titleCaseVersion
      }
    }
  },
  mounted () {
  }
}

export default mixin
